import { inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  ResolveFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable, map } from 'rxjs'

import { selectWorkspaceId } from '@flowx/designer/shared/store/selectors/router.selectors'

import { DetailedPermissions } from '../shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '../workspaces-and-permissions.store'

export const WorkspaceFontsPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['wks_font_create'],
      canEdit: !!permissions['wks_font_edit'],
      canDelete: !!permissions['wks_font_delete'],
      canImport: !!permissions['wks_font_edit'],
      canExport: !!permissions['wks_font_read'],
    }))
  )
}

export const WorkspaceUsersPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAccessRights)
}

export const WorkspaceGroupsPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canRead: !!permissions['wks_groups_read'],
      canCreate: !!permissions['wks_groups_create'],
      canEdit: !!permissions['wks_groups_edit'],
      canDelete: !!permissions['wks_groups_delete'],
    }))
  )
}

export const WorkspaceRolesPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canCreate: !!permissions['wks_roles_create'],
      canEdit: !!permissions['wks_roles_edit'],
      canDelete: !!permissions['wks_roles_delete'],
    }))
  )
}

export const WorkspaceThemesPermissionsResolver: ResolveFn<
  DetailedPermissions
> = (): Observable<DetailedPermissions> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  return toObservable(workspacesAndPermissionsStore.userAndWorkspacePermissions).pipe(
    map((permissions) => ({
      canEdit: !!permissions.theme_edit,
      canRead: !!permissions.theme_read,
      canDelete: !!permissions.theme_delete,
      canCreate: !!permissions.theme_create,
    }))
  )
}

export const Workspace404Activator: CanActivateFn = (): Promise<boolean> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  const availableWorkspaces = workspacesAndPermissionsStore.availableWorkspacesForUser()
  const storedWorkspaceId = workspacesAndPermissionsStore.selectedWorkspaceId()
  const router = inject(Router)

  const selectedWorkspaceId =
    router.currentNavigation()?.extras?.state?.selectedWorkspaceId || storedWorkspaceId

  return !availableWorkspaces.length ||
    !availableWorkspaces.some((workspace) => workspace.id === selectedWorkspaceId)
    ? Promise.resolve(true)
    : router.navigate(['guard', 'workspaces-select'], { queryParams: { canSkipLocation: true } })
}

export const WorkspaceActivator: CanActivateFn = (): Promise<boolean> => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  const store = inject(Store)
  const router = inject(Router)

  const availableWorkspaces = workspacesAndPermissionsStore.availableWorkspacesForUser()
  const workspaceId = store.selectSignal(selectWorkspaceId)()
  const hasAccessToWorkspace = availableWorkspaces.some((workspace) => workspace.id === workspaceId)

  return hasAccessToWorkspace
    ? Promise.resolve(true)
    : router.navigate(['guard', 'workspace', workspaceId, '404'])
}

export const WorkspaceSelectActivator: CanActivateFn = (
  _: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): UrlTree | boolean => {
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  const availableWorkspaces = workspacesAndPermissionsStore.availableWorkspacesForUser()
  const router = inject(Router)

  const userIsOrganizationSaasAdmin = workspacesAndPermissionsStore.userIsOrganizationSaasAdmin()
  if (userIsOrganizationSaasAdmin) {
    return router.createUrlTree(['guard', 'organisation-admin', 'projects'])
  }

  const skipLocation =
    availableWorkspaces.length === 1 && state.url.includes('canSkipLocation=true')

  if (skipLocation) {
    workspacesAndPermissionsStore.changeSelectedWorkspaceId(availableWorkspaces[0].id)
  }
  return skipLocation
    ? router.createUrlTree(['guard', 'workspace', availableWorkspaces[0].id])
    : true
}
